import * as Flux from 'dg-web-shared/lib/Flux.tsx';
import * as Date from 'dg-web-shared/lib/Date.ts';
import { thenElse } from 'dg-web-shared/lib/MaybeV2.ts';
import * as Icons24 from 'dg-web-shared/ui/icons/Icons24.tsx';
import * as Icons16 from 'dg-web-shared/ui/icons/Icons16.tsx';
import { Badge, Permit } from '../../state/ResultState.ts';
import { selectPermit } from '../../actions/ClearancePermitListActions.ts';
import {
    ColumnWidth,
    TableColumn,
    TableRow,
} from '../../../ui/table/Table.tsx';
import { InfoItem } from '../shared/InfoItem.tsx';
import { css } from '@emotion/css';
import { OperatorTypo } from '../../../ui/OperatorTypo.ts';
import {
    getPermitStatusBackground,
    getPermitStatusColor,
} from '../../../ui/permit/permitStatusColors.ts';
import { PermitStatus } from '../../state/PermitsState.ts';
import { ColorHex } from '../../../ui/Colors.ts';
import { IdentificationLicensePlate } from '../../../common/components/IdentificationItems.tsx';
import { clearfix } from 'dg-web-shared/ui/clearfix.ts';
import { Icon16, Icon24 } from '../../../ui/icons/Icon.tsx';
import React, { type JSX } from 'react';

const IdentifierIcon = (props: { icon: JSX.Element }) => (
    <div
        className={css({
            width: '20px',
            paddingTop: '2px',
            color: ColorHex.darkblue,
        })}
    >
        <Icon16 icon={props.icon} />
    </div>
);

interface PermitItemProps {
    permit: Permit;
    update: Flux.Updater;
    key?: React.Key;
}

export const permitStatusIcon = (p: Permit) => {
    switch (p.status) {
        case PermitStatus.active:
            return Icons24.permitState.active;
        case PermitStatus.stornod:
            return Icons24.permitState.canceled;
        case PermitStatus.future:
            return Icons24.permitState.future;
        default:
            return Icons24.permitState.expired;
    }
};

export function PermitItem({ permit, update }: PermitItemProps) {
    function renderLicensePlates(): JSX.Element[] {
        return permit.licensePlates.map(lp => (
            <IdentificationLicensePlate key={lp.id} {...lp} inlineMode={true} />
        ));
    }

    function renderBadges(): JSX.Element[] {
        return permit.badges.map((badge: Badge) => (
            <div key={badge.id} className={clearfix}>
                <IdentifierIcon icon={Icons16.identifier.badge} />
                <div className={css(OperatorTypo.body)}>{badge.labelNr}</div>
            </div>
        ));
    }

    const p = permit;
    const validFrom = thenElse(
        p.validFrom,
        v => Date.Formatter.dayMonthYear(Date.Parser.isoToDateTime(v)),
        '',
    );
    const validTo = thenElse(
        p.validTo,
        v =>
            Date.Parser.isoToDateTime(v) <
            Date.Parser.isoToDateTime('2030-01-01')
                ? Date.Formatter.dayMonthYear(Date.Parser.isoToDateTime(v))
                : '',
        '',
    );

    return (
        <TableRow
            onClick={() =>
                update(store => selectPermit(store, p.contractExternalId))
            }
            className={css({
                ...OperatorTypo.body,
                ...getPermitStatusBackground(p.status, true),
                color: ColorHex.darkblue,
            })}
        >
            <TableColumn width={ColumnWidth._24px} />
            <TableColumn width={ColumnWidth._192px} noPadding={true}>
                <div
                    className={css({
                        display: 'flex',
                        paddingLeft: '4px',
                        alignItems: 'center',
                    })}
                >
                    <div
                        className={css({
                            color: getPermitStatusColor(p.status),
                            width: '32px',
                            paddingTop: '2px',
                        })}
                    >
                        <Icon24 icon={permitStatusIcon(p)} />
                    </div>
                    <div>
                        {renderLicensePlates()}
                        {renderBadges()}
                    </div>
                </div>
            </TableColumn>
            <TableColumn width={ColumnWidth._72px}>{validFrom}</TableColumn>
            <TableColumn width={ColumnWidth._72px}>{validTo}</TableColumn>
            <TableColumn width={ColumnWidth.variable}>
                {permit.permitTypeName}
            </TableColumn>
            <TableColumn width={ColumnWidth._312px}>
                <InfoItem
                    icon={Icons16.identifier.permitId}
                    value={permit.id.toString()}
                />
                <InfoItem
                    icon={Icons16.identifier.infoField}
                    value={permit.additionalInfos}
                />
                <InfoItem
                    icon={Icons16.identifier.infoField}
                    value={permit.remarks}
                />
            </TableColumn>
            <TableColumn width={ColumnWidth._24px} captionFontSize={true}>
                <div
                    className={css({
                        paddingTop: '7px',
                    })}
                >
                    <Icon16 icon={Icons16.chevronRight} />
                </div>
            </TableColumn>
        </TableRow>
    );
}
